import { fabric } from "fabric";
import t from "translations";
import {getCanvasZoom} from 'utils/canvas';

export default class DeskpadCutZone {
  constructor(canvas, width, height, radius) {
    this.canvas = canvas;
    this.width = width;
    this.height = height;
    this.radius = radius;

    canvas.cutZone = this;
    canvas.cutW = width;
    canvas.cutH = height;
    canvas.cutRadius = radius;

    this.defaults = {
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingFlip: true,
      lockScalingX: true,
      lockScalingY: true,
      hasControls: false,
      hasRotatingPoint: false,
      selectable: false,
      evented: false,
      objectCaching: true,
      excludeFromExport: true,
    };
    this.init();
  }

  init = () => {
    this.removeCut();
    this.initBounds();
    // this.initGrid();
    this.initClipZone();
  };

  initGrid = () => {
    const { width, height } = this;
    let dist = 50; // 10 px == 1 cm
    if (width > 1000 || height > 1000) {
      dist = 100;
    }
    if (width > 3000 || height > 3000) {
      dist = 300;
    }
    if (width > 6000 || height > 6000) {
      dist = 600;
    }

    const options = {
      distance: dist,
      width: width + dist * 4,
      height: height + dist * 4,
      x: 0,
      y: 0,
      param: {
        stroke: "#ebebeb",
        strokeWidth: width / 2100,
        selectable: false,
        id: "gridline",
        objectCaching: true,
        evented: false,
      },
    };
    const gridArr = [];
    // const gridLen = options.width / options.x;
    let i = 0;
    for (
      let x = options.x;
      x <= options.width + options.x;
      x += options.distance
    ) {
      const vertical = new fabric.Line(
        [x, options.y, x, options.y + options.height],
        options.param
      );
      if (i % 2 === 0) {
        vertical.set({ stroke: "#ccc" });
      }
      gridArr.push(vertical);
      i++;
    }
    i = 0;
    for (
      let y = options.y;
      y <= options.height + options.y;
      y += options.distance
    ) {
      const horizontal = new fabric.Line(
        [options.x, y, options.x + options.width, y],
        options.param
      );
      if (i % 2 === 0) {
        horizontal.set({ stroke: "#ccc" });
      }
      gridArr.push(horizontal);
      i++;
    }

    this.grid = new fabric.Group(gridArr, {
      left: -dist * 2,
      top: -dist * 2,
      id: "grid",
      ...this.defaults,
    });
    this.canvas.add(this.grid);
  };

  initClipZone = () => {
    const { canvas, width, height } = this;
    const defaultCutSettings = { cutEnabled: true, cutSize: 3 };
    const settings = { ...defaultCutSettings, ...canvas.settings };
    const { cutEnabled, cutSize } = settings;
    this.clipingRect = new fabric.Rect({
      originX: "left",
      originY: "top",
      top: 0,
      left: 0,
      height: height + (cutEnabled && cutSize * 2),
      width: width + (cutEnabled && cutSize * 2),
      fill: canvas.bgColor ? canvas.bgColor : "#fff",
      selectable: false,
      globalCompositeOperation: "source-over",
    });
    canvas.backgroundColor = "rgba(237,240,242,0.4)";
    canvas.setBackgroundImage(this.clipingRect);
  };

  setBackgroundColor = (hex) => {
    this.clipingRect.set({ fill: hex });
    this.canvas.requestRenderAll();
    this.canvas.bgColor = hex;
    this.canvas.fire("canvas:color");
  };

  initBounds = () => {
    const canvas = this.canvas;

    //NOTE: it's needed for migration old canvases without those settigns
    const defaultCutSettings = { cutEnabled: true, cutSize: 3 };
    const settings = { ...defaultCutSettings, ...canvas.settings };
    const { cutEnabled, cutSize } = settings;
    canvas.remove(this.cutzone);
    const { cutW, cutH } = canvas;
    const stroke = cutW > cutH ? cutW / 1000 : cutH / 1000;
    const strokeWidth = stroke > 1.5 ? 1.5 : stroke;
    this.cutObj = new fabric.Rect({
      left: cutSize,
      top: cutSize,
      rx: this.radius,
      ry: this.radius,
      width: this.width,
      height: this.height,
      stroke: "#f0f",
      strokeWidth,
      fill: "rgba(0,0,0,0)",
      objectCaching: false,
      id: "cut",
    });
    this.boundObj = new fabric.Rect({
      left: 0,
      top: 0,
      width: this.width + (cutEnabled && cutSize * 2),
      height: this.height + (cutEnabled && cutSize * 2),
      //strokeDashArray: [15, 1],
      stroke: "rgba(200,200,200,1)",
      strokeWidth,
      fill: "rgba(0,0,0,0)",
      objectCaching: false,
      id: "bound",
    });

    const cutzoneObjs = [this.boundObj];
    if (cutEnabled) {
      cutzoneObjs.push(this.cutObj);
    }

    this.cutzone = new fabric.Group(cutzoneObjs, {
      left: 0,
      top: 0,
      ...this.defaults,
      id: "cutzone",
      objectCaching: false,
    });
    canvas.add(this.cutzone);
    canvas.fire("cutzone:updated", {
      width: canvas.cutW,
      height: canvas.cutH,
      radius: 0,
    });
  };

  hideCutZone = () => {
    this.cutzone.visible = false;
    this.canvas.requestRenderAll();
  };

  showCutZone = () => {
    this.cutzone.visible = true;
    this.canvas.requestRenderAll();
  };

  removeCut = () => {
    const { canvas } = this;
    canvas.remove(this.grid);
    canvas.remove(this.cutzone);
    canvas
      .getObjects()
      .filter((o) => o.id === "cutzone")
      .forEach((o) => canvas.remove(o));
  };

  fitContentToCut = () => {
    const { canvas, width, height } = this;
    const kW = canvas.cutW / width;
    const kH = canvas.cutH / height;
    canvas
      .getObjects()
      .filter((obj) => !obj.excludeFromExport)
      .filter((obj) => obj.mask)
      .forEach((obj) => {
        if (obj.scalable) {
          const center = obj.getPointByOrigin("center", "center");
          obj.setPositionByOrigin(
            { x: center.x / kW, y: center.y / kH },
            "center",
            "center"
          );
        } else {
          obj.left /= kW;
          obj.top /= kH;
          obj.scaleX /= kW;
          obj.scaleY /= kH;
        }
        obj.setCoords();
      });
    canvas
      .getObjects()
      .filter((obj) => !obj.excludeFromExport)
      .filter((obj) => !obj.mask)
      .forEach((obj) => {
        const { clipId } = obj;
        if (clipId) {
          const avgK = (kW + kH) / 2;
          obj.scaleX /= avgK;
          obj.scaleY /= avgK;
          const center = obj.getPointByOrigin("center", "center");
          obj.setPositionByOrigin(
            { x: center.x / kW, y: center.y / kH },
            "center",
            "center"
          );
        }
        obj.setCoords();
        canvas.fire("object:moving", { target: obj });
      });
  };

  updateSize = (width, height, scaleContentType, ignoreMinMax = false) => {
    const { canvas } = this;
    const { minWidth, minHeight, maxWidth, maxHeight } = canvas.settings;
    width = parseFloat(width);
    height = parseFloat(height);
    if (!ignoreMinMax && document.querySelector('#wpadminbar') === null) {
      if (width < minWidth * 10 || width > maxWidth * 10) {
        throw new Error(`${t.widthShouldBe} ${minWidth}cm - ${maxWidth}cm`);
      }
      if (height < minHeight * 10 || height > maxHeight * 10) {
        throw new Error(`${t.heightShouldBe} ${minHeight}cm - ${maxHeight}cm`);
      }
    }
    // this.canvas.remove(this.clipingRect);

    const oldCanvasPlaceholder = new fabric.Rect({
      left: 0,
      top: 0,
      width: this.width,
      height: this.height,
      stroke: "rgba(200,200,200,1)",
      strokeWidth: 1,
      fill: "rgba(0,0,0,0)",
      objectCaching: false,
      id: "oldCanvasPlaceholder",
    });

    this.width = width;
    this.height = height;

    if (scaleContentType === "scale") {
      // canvas.add(oldCanvasPlaceholder);
      this.fitContentToCut();
      // canvas.remove(oldCanvasPlaceholder);
    }

    canvas.cutW = width;
    canvas.cutH = height;
    this.removeCut();
    this.initBounds();
    // this.initGrid();
    this.initClipZone();


    if (scaleContentType === "fit") {
      // canvas.add(oldCanvasPlaceholder);
      this.fitContent();
      // canvas.remove(oldCanvasPlaceholder);
    }

    canvas.requestRenderAll();
    canvas.fire("cutzone:updated", {
      width,
      height,
      radius: canvas.cutRadius,
    });
  };

  fitContent = () => {
    const { canvas } = this;
    canvas.discardActiveObject();
    const existingObjects = canvas
      .getObjects()
      .filter((obj) => !obj.excludeFromExport);
    const sel = new fabric.ActiveSelection(existingObjects, {
      canvas: canvas,
    });
    sel.scale(5);
    canvas.setActiveObject(sel);
    canvas.requestRenderAll();
    canvas.getActiveObject().fitToCutZone();
    canvas.discardActiveObject();
    const cutzoneObj = canvas.getObjects().find((obj) => obj.id === "cutzone");
    if (cutzoneObj) {
      cutzoneObj.moveTo(1000);
    }
  };

  setCutRadius = (radius) => {
    const canvas = this.canvas;
    this.cutObj.set({ rx: radius, ry: radius });
    this.radius = radius;
    canvas.cutRadius = radius;
    canvas.requestRenderAll();
    canvas.fire("cutzone:updated", {
      width: canvas.cutW,
      height: canvas.cutH,
      radius,
    });
  };

  focusCutZone = (zoom) => {
    const canvas = this.canvas;
    if (!zoom) {
      zoom = canvas.getZoom();
    }
    const { width, height, cutW, cutH, settings } = canvas;
    const { cutSize = 0 } = settings;
    const w = cutW + cutSize * 2;
    const h = cutH + cutSize * 2;

    const vpw = canvas.width;
    const vph = canvas.height;
    const cx = (w * zoom) / 2;
    const cy = (h * zoom) / 2;
    const x = cx - vpw / 2;
    const y = cy - vph / 2 + 12;
    canvas.setZoom(zoom);
    canvas.absolutePan({ x, y });
  };

  getSquare = () => {
    const { cutW, cutH } = this.canvas;
    return cutW * cutH;
  };

  getSquareM2 = () => this.getSquare() * 0.001 * 0.001;

  fitCutZone = () => {
    const canvas = this.canvas;
    const {full} = getCanvasZoom(canvas);
    console.log(getCanvasZoom(canvas));

    canvas.setZoom(full*0.9);
    this.focusCutZone();
    canvas.fire("canvas:zoom");
    canvas.fire("canvas:viewport");
  };
}
